/**
 * Centralized Permissions Utility
 *
 * This file consolidates all permission-related functionality including:
 * - Menu item permissions
 * - Page/route permissions
 * - Component permissions
 *
 * Note: Role checking functions and ROLES constants are in roleUtils.ts
 */

import React from 'react';
import { AuthUser } from '../auth/authService';
import { ROLES, hasRole, hasAnyRole } from './roleUtils';

// Re-export ROLES and role checking functions for convenience
export { ROLES, hasRole, hasAnyRole, hasAllRoles } from './roleUtils';

/**
 * Menu Item Interface
 * Defines the structure for menu items with permission rules
 */
export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon?: string | React.ReactNode;
  requiredRoles?: string[]; // Roles that can see this menu item
  excludedRoles?: string[]; // Roles that should NOT see this menu item
  /** When true, only users at a SPOKE facility see this item. */
  requireSpokeFacility?: boolean;
}

/**
 * Check if a menu item should be visible for a user
 */
export function isMenuItemVisible(user: AuthUser | null, menuItem: MenuItem): boolean {
  // If no user, hide all items
  if (!user) {
    return false;
  }

  // If excludedRoles is specified and user has any excluded role, hide the item
  if (menuItem.excludedRoles && menuItem.excludedRoles.length > 0) {
    const hasExcludedRole = hasAnyRole(user, menuItem.excludedRoles);

    // Debug logging for Users menu item
    if (menuItem.id === 'users' && process.env.NODE_ENV === 'development') {
      console.log('isMenuItemVisible - Users menu check:', {
        menuItemId: menuItem.id,
        excludedRoles: menuItem.excludedRoles,
        userRoles: user.roles,
        hasExcludedRole,
        hasRoleResult: menuItem.excludedRoles.map(role => ({
          role,
          hasRole: hasRole(user, role),
        })),
      });
    }

    if (hasExcludedRole) {
      return false;
    }
  }

  // If requiredRoles is specified, user must have at least one of them
  if (menuItem.requiredRoles && menuItem.requiredRoles.length > 0) {
    if (!hasAnyRole(user, menuItem.requiredRoles)) {
      return false;
    }
  }

  if (menuItem.requireSpokeFacility && !isSpokeFacilityUser(user)) {
    return false;
  }

  // If no restrictions, show the item
  return true;
}

/**
 * Filter menu items based on user roles
 */
export function filterMenuItemsByRole(user: AuthUser | null, menuItems: MenuItem[]): MenuItem[] {
  const filtered = menuItems.filter(item => {
    const isVisible = isMenuItemVisible(user, item);

    // Debug logging for Users menu specifically
    if (item.id === 'users' && process.env.NODE_ENV === 'development') {
      console.log('filterMenuItemsByRole - Users menu:', {
        itemId: item.id,
        excludedRoles: item.excludedRoles,
        isVisible,
        userRoles: user?.roles,
        hasSuperAdmin: hasRole(user, ROLES.SUPER_ADMIN),
      });
    }

    return isVisible;
  });

  if (process.env.NODE_ENV === 'development') {
    console.log('filterMenuItemsByRole - Result:', {
      totalItems: menuItems.length,
      filteredCount: filtered.length,
      filteredIds: filtered.map(item => item.id),
      userHasSuperAdmin: hasRole(user, ROLES.SUPER_ADMIN),
    });
  }

  return filtered;
}

/**
 * Page Permission Configuration
 * Defines which roles can access which routes/pages
 */
export interface PagePermission {
  path: string;
  requiredRoles?: string[]; // User must have at least one of these roles
  excludedRoles?: string[]; // User must NOT have any of these roles
  allowAll?: boolean; // If true, all authenticated users can access
  /** When true, only users at a SPOKE facility may access (e.g. DICOM routing rules). */
  requireSpokeFacility?: boolean;
}

/**
 * Page permissions configuration
 * Add new pages here with their permission rules
 */
export const PAGE_PERMISSIONS: PagePermission[] = [
  {
    path: '/',
    allowAll: true, // Studies page - available to all authenticated users
  },
  {
    path: '/peer-reviews',
    requiredRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.RIS_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_QUALITY_MANAGER,
    ],
  },
  {
    path: '/abha-consents',
    requiredRoles: [ROLES.RIS_RADIOLOGIST],
  },
  {
    path: '/templates',
    requiredRoles: [ROLES.SUPER_ADMIN, ROLES.RIS_RADIOLOGIST],
  },
  {
    path: '/facilities',
    excludedRoles: [
      ROLES.RIS_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_REFERRING_PHYSICIAN,
    ],
  },
  {
    path: '/users',
    excludedRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_REFERRING_PHYSICIAN,
    ],
  },
  {
    path: '/facility-details',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    path: '/profile',
    allowAll: true, // Profile page - available to all authenticated users
  },
  {
    path: '/admin-dashboard',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    path: '/billing-reconciliation',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/machines',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    path: '/analytics/collection-reconciliation',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    // Intentional: Statewide KPIs is ris-super-admin only (state-level view).
    // ris-admin and ris-district-admin retain other analytics routes (operational, billing, etc.).
    path: '/analytics/statewide-kpis',
    requiredRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/analytics/operational',
    requiredRoles: [ROLES.RIS_ADMIN, ROLES.RIS_HUB_ADMIN, ROLES.RIS_RADIOLOGY_MANAGER],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/analytics/ai-dashboard',
    requiredRoles: [ROLES.SUPER_ADMIN, ROLES.RIS_ADMIN],
  },
  {
    path: '/analytics/report-delivery',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/analytics/notification-delivery',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/analytics/peer-review',
    requiredRoles: [ROLES.RIS_ADMIN, ROLES.RIS_RADIOLOGIST],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/analytics',
    requiredRoles: [
      ROLES.RIS_ADMIN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.SUPER_ADMIN,
      ROLES.RIS_DISTRICT_ADMIN,
      ROLES.RIS_HUB_ADMIN,
      ROLES.RIS_RADIOLOGY_MANAGER,
      ROLES.RIS_QUALITY_MANAGER,
    ],
  },
  {
    path: '/ris-configuration',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    path: '/escalation-monitor',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/critical-results-dashboard',
    requiredRoles: [ROLES.RIS_ADMIN],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/acknowledgement-audit-trail',
    requiredRoles: [ROLES.RIS_ADMIN, ROLES.RIS_QUALITY_MANAGER],
    excludedRoles: [ROLES.SUPER_ADMIN],
  },
];

/** True when the signed-in user's facility is type SPOKE (DICOM routing rules are spoke-only). */
export function isSpokeFacilityUser(user: AuthUser | null): boolean {
  const facilityType = user?.facility?.facility_type;
  if (!facilityType) {
    return false;
  }
  return String(facilityType).trim().toUpperCase() === 'SPOKE';
}

/**
 * Check if user has permission to access a specific page/route
 */
export function canAccessPage(user: AuthUser | null, path: string): boolean {
  if (!user) {
    return false;
  }

  // Find permission configuration for this path (exact or analytics prefix)
  let permission = PAGE_PERMISSIONS.find(p => p.path === path);
  if (!permission && path.startsWith('/analytics/')) {
    permission = PAGE_PERMISSIONS.find(
      p => path.startsWith(p.path) && p.path.startsWith('/analytics')
    );
  }

  // If no permission config found, default to allowing access (for backward compatibility)
  if (!permission) {
    return true;
  }

  // If allowAll is true, allow access
  if (permission.allowAll) {
    return true;
  }

  // Check excluded roles
  if (permission.excludedRoles && permission.excludedRoles.length > 0) {
    if (hasAnyRole(user, permission.excludedRoles)) {
      return false;
    }
  }

  // Check required roles
  if (permission.requiredRoles && permission.requiredRoles.length > 0) {
    if (!hasAnyRole(user, permission.requiredRoles)) {
      return false;
    }
  }

  if (permission.requireSpokeFacility && !isSpokeFacilityUser(user)) {
    return false;
  }

  // If no restrictions specified, allow access
  return true;
}

/** Critical Results dashboard overview and drill-down list pages. */
export function canAccessCriticalResultsDashboard(user: AuthUser | null): boolean {
  return canAccessPage(user, '/critical-results-dashboard');
}

export function canAccessAcknowledgementAuditTrail(user: AuthUser | null): boolean {
  return canAccessPage(user, '/acknowledgement-audit-trail');
}

export function canExportAcknowledgementAuditTrail(user: AuthUser | null): boolean {
  return hasAnyRole(user, [ROLES.RIS_QUALITY_MANAGER, ROLES.RIS_ADMIN]);
}

/**
 * Who may submit a peer review (remark + comments).
 * Admin / Super Admin / Technician may open Peer Review and View only.
 */
export function canPerformPeerReview(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }
  return hasAnyRole(user, [ROLES.RIS_RADIOLOGIST, ROLES.RIS_QUALITY_MANAGER]);
}

/**
 * Hide the facility radiologist picker. Server scopes radiologist analytics from the JWT
 * (`original_radiologist_id`), not this flag. Dual-role ris-admin + ris-radiologist keeps
 * facility-wide analytics (admin wins), matching RIS middleware — unlike GET /peer-reviews,
 * where radiologist wins so dual-role users still see their assignment worklist.
 */
export function isPeerReviewAnalyticsRadiologistScoped(user: AuthUser | null): boolean {
  return hasRole(user, ROLES.RIS_RADIOLOGIST) && !hasRole(user, ROLES.RIS_ADMIN);
}

/**
 * For users who can access the admin dashboard: swap nav paths so the home icon opens the
 * admin dashboard and the dashboard icon opens the studies worklist (`/`).
 * Icons stay distinct (Home vs LayoutDashboard) so both remain visible in the sidebar.
 * Other users keep Studies → `/` and do not see the Admin Dashboard item.
 */
export function applyAdminNavPathSwap(user: AuthUser | null, menuItems: MenuItem[]): MenuItem[] {
  if (!user || !canAccessPage(user, '/admin-dashboard')) {
    return menuItems;
  }
  return menuItems.map(item => {
    if (item.id === 'studies') {
      return {
        ...item,
        path: '/admin-dashboard',
        label: 'Home',
        icon: 'LayoutDashboard',
      };
    }
    if (item.id === 'admin-dashboard') {
      return {
        ...item,
        path: '/',
        label: 'Studies',
        icon: 'TabStudies',
      };
    }
    return item;
  });
}

/**
 * Component Permission Configuration
 * Defines which roles can see/use specific components
 */
export interface ComponentPermission {
  componentId: string;
  requiredRoles?: string[];
  excludedRoles?: string[];
  allowAll?: boolean;
}

/**
 * Component permissions configuration
 * Add new components here with their permission rules
 */
export const COMPONENT_PERMISSIONS: ComponentPermission[] = [
  // Add component permissions here as needed
  // Example:
  // {
  //   componentId: 'admin-panel',
  //   requiredRoles: [ROLES.RIS_ADMIN],
  // },
];

/**
 * Check if user has permission to see/use a specific component
 */
export function canUseComponent(user: AuthUser | null, componentId: string): boolean {
  if (!user) {
    return false;
  }

  // Find permission configuration for this component
  const permission = COMPONENT_PERMISSIONS.find(p => p.componentId === componentId);

  // If no permission config found, default to allowing access
  if (!permission) {
    return true;
  }

  // If allowAll is true, allow access
  if (permission.allowAll) {
    return true;
  }

  // Check excluded roles
  if (permission.excludedRoles && permission.excludedRoles.length > 0) {
    if (hasAnyRole(user, permission.excludedRoles)) {
      return false;
    }
  }

  // Check required roles
  if (permission.requiredRoles && permission.requiredRoles.length > 0) {
    return hasAnyRole(user, permission.requiredRoles);
  }

  // If no restrictions specified, allow access
  return true;
}

/**
 * ============================================================================
 * MENU CONFIGURATION
 * ============================================================================
 * All menu items with their permissions are defined here.
 * This is the single source of truth for menu items across the application.
 */

/**
 * Interface for NavigationSidebar menu items
 */
export interface NavigationMenuItem {
  title: string;
  path: string;
  icon?: string;
}

/**
 * All menu items with their permissions
 * This is the single source of truth for menu items across the application
 */
export const ALL_MENU_ITEMS: MenuItem[] = [
  {
    id: 'studies',
    label: 'Studies',
    path: '/',
    icon: 'TabStudies',
    // Available to all authenticated users
  },
  {
    id: 'abha-consents',
    label: 'ABDM Consents',
    path: '/abha-consents',
    icon: 'ShieldCheck',
    requiredRoles: [ROLES.RIS_RADIOLOGIST],
  },
  {
    id: 'peer-reviews',
    label: 'Peer review',
    path: '/peer-reviews',
    icon: 'ClipboardCheck',
    requiredRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.RIS_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_QUALITY_MANAGER,
    ],
  },
  {
    id: 'templates',
    label: 'Templates',
    path: '/templates',
    icon: 'ListView',
    requiredRoles: [ROLES.SUPER_ADMIN, ROLES.RIS_RADIOLOGIST],
  },
  {
    id: 'facilities',
    label: 'Facilities',
    path: '/facilities',
    icon: 'Settings',
    // Hide from ris-admin, ris-technician, ris-radiologist, and ris-referring-physician
    excludedRoles: [
      ROLES.RIS_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_REFERRING_PHYSICIAN,
    ],
  },
  {
    id: 'users',
    label: 'Users Management',
    path: '/users',
    icon: 'User',
    // Hide from superadmin, ris-technician, ris-radiologist, and ris-referring-physician
    excludedRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.RIS_TECHNICIAN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.RIS_REFERRING_PHYSICIAN,
    ],
  },
  {
    id: 'facility-details',
    label: 'Facility Details',
    path: '/facility-details',
    icon: 'Settings',
    // Only visible to ris-admin
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    id: 'admin-dashboard',
    label: 'Admin Dashboard',
    path: '/admin-dashboard',
    icon: 'LayoutDashboard',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/analytics/collection-reconciliation',
    icon: 'BarChart3',
    requiredRoles: [
      ROLES.RIS_ADMIN,
      ROLES.RIS_RADIOLOGIST,
      ROLES.SUPER_ADMIN,
      ROLES.RIS_DISTRICT_ADMIN,
      ROLES.RIS_HUB_ADMIN,
      ROLES.RIS_RADIOLOGY_MANAGER,
      ROLES.RIS_QUALITY_MANAGER,
    ],
  },
  {
    id: 'ris-configuration',
    label: 'RIS Configuration',
    path: '/ris-configuration',
    icon: 'Configuration',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
  {
    id: 'machines',
    label: 'Machines',
    path: '/machines',
    icon: 'Settings',
    requiredRoles: [ROLES.RIS_ADMIN],
  },
];

/**
 * Convert MenuItem to NavigationMenuItem format
 */
export function menuItemToNavigationMenuItem(item: MenuItem): NavigationMenuItem {
  return {
    title: item.label,
    path: item.path,
    icon: typeof item.icon === 'string' ? item.icon : undefined,
  };
}
